import { CheckOutlined, EditOutlined } from '@ant-design/icons'
import type { InputProps } from 'antd'
import type React from 'react'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import ClickAwayListener from 'react-click-away-listener'
import styled, { css } from 'styled-components'
import { Text } from '../../general'
import { Input } from '../Input/Input'
import DeleteIcon from './DeleteIcon.svg'

export type InlineEditProps = {
  placeholder?: InputProps['placeholder']
  value?: string
  onChange?: (newValue: string) => void
  className?: string
  disabled?: boolean
  defaultEditState?: boolean
  textComponent?: (props: { children: React.ReactNode }) => JSX.Element
  isEditing?: boolean
  onEditStateChange?: (editState: boolean) => void
  onCancel?: () => void
  displayActions?: boolean
  setIsEditing?: Dispatch<SetStateAction<boolean>>
}

const InlineEdit = (props: InlineEditProps) => {
  const {
    defaultEditState = false,
    placeholder,
    value,
    onChange,
    className,
    disabled,
    textComponent = Text,
    isEditing: isEditingProps,
    onEditStateChange,
    onCancel,
    displayActions = true,
    setIsEditing
  } = props

  const [isEditing, setEditing] = useState(defaultEditState)

  // the value of the used variable
  const usedIsEditing = isEditingProps || isEditing

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    onEditStateChange?.(usedIsEditing)
    setIsEditing?.(isEditing)
  }, [isEditing, isEditingProps])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (disabled && usedIsEditing) {
      setEditing(false)
    }
  }, [disabled, isEditing, isEditingProps])

  const defaultValue = value || ''

  const [newValue, setNewValue] = useState<string>(defaultValue)
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (value) {
      setNewValue(value)
    }
  }, [value])

  const changeHandler: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const newValueUpdated = event.target.value

    setNewValue(newValueUpdated)
    setNewValue(newValueUpdated)
  }

  const cancelHandler = () => {
    setNewValue(defaultValue)
    setEditing(false)
    onCancel?.()
  }
  const saveHandler = () => {
    if (usedIsEditing) {
      onChange?.(newValue)
      setEditing(false)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      saveHandler()
    }
  }

  const onBlurHandler = () => {
    if (newValue?.length) {
      saveHandler()
    } else {
      cancelHandler()
    }
  }

  return (
    <ClickAwayListener
      // Mihai doesnt like we should find another way
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClickAway={!onEditStateChange ? saveHandler : () => {}}
    >
      <Wrapper className={className}>
        {usedIsEditing ? (
          <>
            {textComponent?.({
              children: (
                <InputStyled
                  autoFocus
                  value={newValue}
                  placeholder={placeholder}
                  onChange={changeHandler}
                  onKeyDown={handleKeyDown}
                  {...(!displayActions
                    ? {
                        onBlur: onBlurHandler
                      }
                    : {})}
                />
              )
            })}
            {displayActions && (
              <>
                <CheckOutlinedStyled onClick={saveHandler} />
                <CancelIcon src={DeleteIcon} onClick={cancelHandler} />
              </>
            )}
          </>
        ) : (
          <>
            {textComponent?.({ children: newValue })}
            {disabled || (
              <EditOutlinedStyled onClick={() => setEditing(true)} />
            )}
          </>
        )}
      </Wrapper>
    </ClickAwayListener>
  )
}

const InputStyled = styled(Input)`
  font: inherit;
`
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const iconCSS = css`
  cursor: pointer;
  width: 20px;
  height: 20px;
`

const CancelIcon = styled.img`
  ${iconCSS}

  color: ${({ theme }) => theme.color.status.danger};
`
const CheckOutlinedStyled = styled(CheckOutlined)`
  ${iconCSS}

  color: ${({ theme }) => theme.color.status.success};
`

const EditOutlinedStyled = styled(EditOutlined)`
  margin-left: 0px;
  path {
    fill: ${({ theme }) => theme.color.neutral.color5};
  }
  &:hover {
    path {
      fill: ${({ theme }) => theme.color.status.primary};
    }
  }
`

export default InlineEdit
